import { FC } from "react";
import { useTranslation } from "react-i18next";
import Button from "../ui/Button";
import { ButtonVariant } from "../../types/constants";
import NewsMetaInfo from "./NewsMetaInfo";
import ImagePlaceholderSVG from "../shared/svg/ImagePlaceholder";
import { INewsFlat } from "../../domain/models/News";
import HtmlContent from "../../custom-components/HtmlContent";

interface NewsContentSectionProps {
    news: INewsFlat;
    onShare: () => void;
}

const NewsContentSection: FC<NewsContentSectionProps> = ({ news, onShare }) => {
    const { t } = useTranslation();

    const imageElement = news.pictureExists?.() ? (
        <img src={news.getPictureUrl?.() || ""} alt={news.title} className="w-full h-full object-cover rounded-[6px]" />
    ) : (
        <ImagePlaceholderSVG classname="w-full h-full" />
    );

    return (
        <section className="w-full max-w-[1280px] mx-auto">
            <h1 className="font-geologica font-medium text-[36px] leading-[1] uppercase text-primary-400 mb-[42px]">
                {news.title}
            </h1>

            <div className="md:hidden">
                <div className="w-full h-[300px] mb-[24px]">{imageElement}</div>

                <NewsMetaInfo news={news} />

                {news.shortContent && (
                    <p className="text-text-300 text-[16px] leading-[1.3] mb-[32px]">{news.shortContent}</p>
                )}

                <div className="mb-[22px]">
                    <Button text={t("buttons.share")} variant={ButtonVariant.SHARE} onClick={onShare} />
                </div>

                {news.content && (
                    <div className="mb-[100px]">
                        <HtmlContent html={news.content} className="text-text-300 text-[16px] leading-[1.3]" />
                    </div>
                )}
            </div>

            <div className="hidden md:block xl:hidden">
                <NewsMetaInfo news={news} />

                {news.shortContent && (
                    <p className="text-text-300 text-[16px] leading-[1.3] mb-[22px]">{news.shortContent}</p>
                )}

                <div className="w-full max-w-[700px] h-[445px] mb-[42px]">{imageElement}</div>

                <div className="mb-[22px]">
                    <Button text={t("buttons.share")} variant={ButtonVariant.SHARE} onClick={onShare} />
                </div>

                {news.content && (
                    <div className="mb-[100px]">
                        <HtmlContent html={news.content} className="text-text-300 text-[16px] leading-[1.3]" />
                    </div>
                )}
            </div>

            <div className="hidden xl:block">
                <div className="flex gap-[42px] mb-[22px]">
                    <div className="w-[548px] flex flex-col">
                        <NewsMetaInfo news={news} />

                        {news.shortContent && (
                            <p className="text-text-300 text-[16px] leading-[1.3] mb-[32px]">{news.shortContent}</p>
                        )}

                        <div className="mt-auto">
                            <Button text={t("buttons.share")} variant={ButtonVariant.SHARE} onClick={onShare} />
                        </div>
                    </div>

                    <div className="w-[671px] h-[510px] flex-shrink-0">{imageElement}</div>
                </div>

                {news.content && (
                    <div className="mb-[100px]">
                        <HtmlContent html={news.content} className="text-text-300 text-[16px] leading-[1.3]" />
                    </div>
                )}
            </div>
        </section>
    );
};

export default NewsContentSection;
